/**
 * Halloween embroidery patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenEmbroideryPatternsPack: ToilePack = {
    slug: 'halloween-embroidery-patterns',
    category: 'season',
    name: 'Halloween embroidery patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'wreath', label: 'Pumpkin wreath', subject: 'A round wreath of pumpkins and autumn leaves'},
        {key: 'house', label: 'Haunted house', subject: 'A haunted house with a moon behind it'},
        {key: 'ghosts', label: 'Row of ghosts', subject: 'A row of three small ghosts holding hands'},
    ],
    styles: [
        {key: 'simple', label: 'Simple outline', style: 'a bold single-line running-stitch transfer outline, wide uniform stroke', palette: 'Black ink only', framing: 'Centered on a pure white page with a wide margin'},
        {key: 'detailed', label: 'Detailed pattern', style: 'a detailed pattern with fine double-line transfer guides and dense satin-stitch fill hatching', palette: 'Black ink only', framing: 'Filling the page within a moderate white margin'},
        {key: 'redwork', label: 'Redwork style', style: 'a thin uniform redwork-style outline, delicate folk-art linework', palette: 'Black ink only', framing: 'Centered on a pure white page with a wide margin'},
    ],
    directions: ['a monogram instead', 'for a tea towel', 'as a mini hoop design'],
    directionHint: 'try: a monogram instead',
    seo: {
        title: 'Halloween Embroidery: Pumpkin Wreath, House, Ghosts',
        metaDescription: 'Nine halloween embroidery patterns — a pumpkin wreath, a haunted house, a row of ghosts — stitch lines, black on white, three styles.',
        h1: 'Halloween embroidery pattern: pumpkin wreath, haunted house, ghosts',
        tagline: 'Nine patterns, three October subjects, three styles each.',
        intro: 'Transfer-ready patterns for the hoop: a pumpkin wreath, a haunted house, a row of ghosts, each drawn as clean stitch lines in black on white. Ask for a spider web, a black cat, or your own subject, and the same three styles carry it.',
        useCases: [
            'a hand embroidery hoop project',
            'an iron-on transfer for stitching',
            'a Halloween craft fair item',
            'a handmade tote bag or patch design',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine embroidery-style patterns — a pumpkin wreath, a haunted house, a row of ghosts — drawn as stitch lines in black on white, in three styles.'},
            {q: 'Can I transfer this directly onto fabric?', a: "The clean black-on-white line style is built to transfer via tracing, iron-on transfer paper, or a light box — check your chosen transfer method's own steps."},
            {q: 'Does this include stitch-type instructions, like backstitch or satin stitch?', a: 'No — it provides the pattern lines only; choosing which stitches to use for each section is up to the embroiderer.'},
            {q: 'Can I get a different subject, like a spider web or black cat?', a: 'Yes — the direction field takes any Halloween subject, including a spider web or a black cat.'},
            {q: 'Can I sell embroidered items made from this pattern?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
